import { getReferenceLineDisplayName, type ReferenceLineConfig } from './reference-lines';

/** The plot area in CSS pixels of the canvas. */
export type ReferenceLineLabelsArea = { x: number; y: number; w: number; h: number };

export type ReferenceLineLabel = {
  /** The line name, truncated with an ellipsis to fit the plot area. */
  text: string;
  color: string;
  /** The right end of the text. */
  x: number;
  /** The vertical middle of the text. */
  y: number;
};

type LabelPlacement = { text: string; color: string; top: number };

const LABEL_LINE_HEIGHT = 1.2;
const LABEL_INSET_X = 4;
const LABEL_GAP_Y = 2;
const LABEL_HALO_WIDTH = 3;
const ELLIPSIS = '\u2026';
const NO_LINE_DASH: number[] = [];

function getReferenceLineLabelFont(fontSize: number): string {
  return `${fontSize}px IBM Plex Sans`;
}

/** Shortens the text by whole characters, so that multi-byte characters are never split. */
function truncateToWidth(
  text: string,
  maxWidth: number,
  measureTextWidth: (text: string) => number,
): string | undefined {
  if (measureTextWidth(text) <= maxWidth) {
    return text;
  }

  const characters = Array.from(text);
  const withEllipsis = (length: number) =>
    `${characters.slice(0, length).join('').trimEnd()}${ELLIPSIS}`;

  // The longest prefix that fits with the ellipsis, with at least one character.
  let low = 0;
  let high = characters.length - 1;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);

    if (measureTextWidth(withEllipsis(middle)) <= maxWidth) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return low > 0 ? withEllipsis(low) : undefined;
}

type DrawnLine = {
  lineY: number;
  color: string;
  /** Undefined when not even one character of the name fits. */
  text: string | undefined;
};

/**
 * Places the names of the visible horizontal reference lines whose value is in the plot area,
 * right-aligned at the right end of the plot area. A label is always next to its own line: just
 * above it, or just below it when the space above is not free. Lines are processed from top to
 * bottom, and a space is free when it is inside the plot area, does not overlap a label placed
 * before, and no other drawn reference line, labeled or not, crosses it. A label without a free
 * space is left out; its name is still in the tooltip and the legend. Positions are rounded to
 * device pixels.
 */
export function layoutReferenceLineLabels({
  referenceLines,
  plotArea,
  valueToCanvasY,
  measureTextWidth,
  fontSize,
  devicePixelRatio,
}: {
  referenceLines: ReadonlyArray<ReferenceLineConfig>;
  plotArea: ReferenceLineLabelsArea;
  valueToCanvasY: (value: number) => number;
  measureTextWidth: (text: string) => number;
  fontSize: number;
  devicePixelRatio: number;
}): ReferenceLineLabel[] {
  const labelHeight = Math.ceil(fontSize * LABEL_LINE_HEIGHT);
  const maxTextWidth = plotArea.w - 2 * LABEL_INSET_X;
  const plotTop = plotArea.y;
  const plotBottom = plotArea.y + plotArea.h;

  if (maxTextWidth <= 0 || labelHeight > plotArea.h) {
    return [];
  }

  const lines: DrawnLine[] = [];

  for (const { name, value, color, visible } of referenceLines) {
    if (!visible || value === undefined) {
      continue;
    }

    const lineY = valueToCanvasY(value);

    if (Number.isFinite(lineY) && lineY >= plotTop && lineY <= plotBottom) {
      const text = truncateToWidth(
        getReferenceLineDisplayName(name),
        maxTextWidth,
        measureTextWidth,
      );
      lines.push({ lineY, color, text });
    }
  }

  // The sort is stable, so lines with the same value keep the configuration order.
  lines.sort((a, b) => a.lineY - b.lineY);

  const placements: LabelPlacement[] = [];

  const isFree = (top: number, ownLine: DrawnLine) => {
    const bottom = top + labelHeight;

    return (
      top >= plotTop &&
      bottom <= plotBottom &&
      placements.every(
        (placement) => bottom <= placement.top || top >= placement.top + labelHeight,
      ) &&
      lines.every((line) => line === ownLine || line.lineY <= top || line.lineY >= bottom)
    );
  };

  for (const line of lines) {
    const { lineY, color, text } = line;

    if (text === undefined) {
      continue;
    }

    const top = [lineY - LABEL_GAP_Y - labelHeight, lineY + LABEL_GAP_Y].find((candidateTop) =>
      isFree(candidateTop, line),
    );

    if (top !== undefined) {
      placements.push({ text, color, top });
    }
  }

  const roundToDevicePixels = (value: number) =>
    Math.round(value * devicePixelRatio) / devicePixelRatio;
  const x = roundToDevicePixels(plotArea.x + plotArea.w - LABEL_INSET_X);

  return placements.map(({ text, color, top }) => ({
    text,
    color,
    x,
    y: roundToDevicePixels(top + labelHeight / 2),
  }));
}

/**
 * Draws the labels of horizontal reference lines in their colors, with a halo in the plot
 * background color, so that they stay readable over the series. Draw them after the series.
 * The context must be scaled to CSS pixels.
 */
export function drawReferenceLineLabels({
  ctx,
  referenceLines,
  plotArea,
  valueToCanvasY,
  fontSize,
  haloColor,
  devicePixelRatio,
}: {
  ctx: CanvasRenderingContext2D;
  referenceLines: ReadonlyArray<ReferenceLineConfig>;
  plotArea: ReferenceLineLabelsArea;
  valueToCanvasY: (value: number) => number;
  fontSize: number;
  haloColor: string;
  devicePixelRatio: number;
}): void {
  ctx.save();

  try {
    ctx.font = getReferenceLineLabelFont(fontSize);

    const labels = layoutReferenceLineLabels({
      referenceLines,
      plotArea,
      valueToCanvasY,
      measureTextWidth: (text) => ctx.measureText(text).width,
      fontSize,
      devicePixelRatio,
    });

    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = LABEL_HALO_WIDTH;
    ctx.strokeStyle = haloColor;
    ctx.setLineDash(NO_LINE_DASH);

    for (const { text, color, x, y } of labels) {
      ctx.strokeText(text, x, y);
      ctx.fillStyle = color;
      ctx.fillText(text, x, y);
    }
  } finally {
    ctx.restore();
  }
}
